import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useField, useFormikContext } from 'formik';
import styled from 'styled-components';
import { Editor } from '@tinymce/tinymce-react';
import {
  Button,
  ButtonGroup,
  Collapse,
  FormHelperText,
  Grid,
  GridProps,
  Typography
} from '@material-ui/core';
import { Done as DoneIcon, Clear as ClearIcon } from '@material-ui/icons';

import { UserContext } from '../../user/User.context';
import environment from '../../environments';
import AppSnackbar from '../AppSnackbar';
import { useTimeout } from '../timeout.hook';
import { InlineFileValue } from './InlineFile';
import InlineHtmlImageDialog, {
  InlineHtmlImageDialogProps
} from './InlineHtmlImageDialog';
import {
  formatImgURLs,
  useImageReloader,
  useAttachNewImages,
  applyImageStyling
} from './InlineHtml.hooks';

type SetImageCallback = (src: string, options?: object) => void;

const StyledButtonGroup = styled(ButtonGroup)`
  border: 1px solid #ccc;
  border-top: 0;
  border-radius: 0;
`;

export interface InlineHtmlProps
  extends Pick<
    InlineHtmlImageDialogProps,
    'onCompleteUpload' | 'onChangeAttachments'
  > {
  name: string;
  type?: string;
  value?: string;
  label?: string;
  height?: string | number;
  helperText?: string;
  disabled?: boolean;
  enableMedia?: boolean;
  hideConfirm?: boolean;
  submitOnChange?: boolean;
  grid?: GridProps;
  attachments?: InlineFileValue[];
}

const InlineHtml: React.FC<InlineHtmlProps> = (props: InlineHtmlProps) => {
  const {
    value,
    disabled,
    label,
    helperText,
    height,
    enableMedia = false,
    hideConfirm = false,
    submitOnChange = false,
    grid = { xs: 12 } as GridProps,
    attachments = [],
    onChangeAttachments,
    onCompleteUpload
  } = props;
  const noAutoResize: boolean = !!height;
  const formik = useFormikContext();
  const { resetSessionTime } = useContext(UserContext);
  const setImageCallbackRef = useRef<Function | undefined>();
  const textRef = useRef<string>('');
  const [debounceSetValue] = useTimeout();
  const [debounceSubmit, clearDebouncedSubmit] = useTimeout();
  const [field, meta, { setValue: setFieldValue, setTouched }] =
    useField(props);
  const [focus, setFocus] = useState<boolean>(false);
  const [buttonFocus, setButtonFocus] = useState<boolean>(false);
  const [didInit, setDidInit] = useState<boolean>(false);
  const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [previousValue, setPreviousValue] = useState<any>(meta.initialValue);
  const [lastSubmitCount, setLastSubmitCount] = useState<number>(
    formik.submitCount
  );
  const text: string = useMemo(() => {
    return formatImgURLs(value || field.value);
  }, [value, field.value]);
  const { error, reloadFailedImages } = useImageReloader();
  const { inspectAndUpload } = useAttachNewImages({
    attachments,
    onCompleteUpload,
    onChangeAttachments
  });

  const toolbar: string = useMemo(() => {
    const str: string[] = [
      'undo redo | formatselect | bold italic backcolor | alignleft aligncenter alignright alignjustify',
      `image ${enableMedia ? 'media' : ''}`,
      'link bullist numlist outdent indent | removeformat | help | code',
      'table tabledelete | tableprops tablerowprops tablecellprops | tableinsertrowbefore tableinsertrowafter tabledeleterow | tableinsertcolbefore tableinsertcolafter tabledeletecol'
    ];
    return str.join(' | ');
  }, [enableMedia]);

  // fixes infinite re-render for consecutive setValue calls
  const setValue = (value: any) => {
    debounceSetValue(() => setFieldValue(value));
  };

  const handleSubmit = () => {
    setTouched(true);
    setFocus(false);
    formik.submitForm();
  };

  const handleCancel = () => {
    setTouched(false);
    setValue(previousValue);
  };

  const filePickerCallback = (setImageCallback: Function) => {
    setImageCallbackRef.current = setImageCallback;
    setIsDialogOpen(true);
  };

  useEffect(() => {
    textRef.current = text;
  }, [text]);

  useEffect(() => {
    // update previous value if initial value changes
    setPreviousValue(meta.initialValue);
  }, [meta.initialValue]);

  useEffect(() => {
    if (!formik.isSubmitting && lastSubmitCount !== formik.submitCount) {
      // form finished submitting so set current value to previous value
      setPreviousValue(meta.value);
      setLastSubmitCount(formik.submitCount);
    }
  }, [formik.isSubmitting, formik.submitCount, lastSubmitCount, meta.value]);

  useEffect(() => {
    error && setErrorMessage(error);
  }, [error]);

  return (
    <Grid item {...grid}>
      {label && (
        <Typography variant='h5' gutterBottom>
          {label}
        </Typography>
      )}
      {!didInit && (
        <Typography
          variant='body2'
          color='textSecondary'
          style={{ marginTop: 10 }}
        >
          Loading editor...
        </Typography>
      )}
      <Editor
        apiKey={environment.tinyApiKey}
        key={`${disabled}-${noAutoResize}-${enableMedia}`}
        value={text}
        init={{
          height,
          menubar: false,
          contextmenu: false,
          browser_spellcheck: true,
          paste_data_images: true,
          relative_urls: false,
          remove_script_host: false,
          plugins: [
            'advlist autolink lists link image charmap print preview anchor',
            'searchreplace visualblocks code fullscreen',
            'insertdatetime media table paste help wordcount',
            noAutoResize ? '' : 'autoresize',
            enableMedia ? 'media' : '',
            'table'
          ],
          toolbar,
          file_picker_callback: filePickerCallback
        }}
        disabled={disabled}
        onFocus={() => setFocus(true)}
        onInit={(event: any) => {
          const iframe: HTMLIFrameElement | undefined =
            event.target?.iframeElement;
          applyImageStyling(iframe);
          reloadFailedImages(iframe);
          setDidInit(true);
        }}
        onBlur={() => debounceSubmit(() => handleSubmit(), 100)}
        onEditorChange={async (content: string | undefined, editor: any) => {
          reloadFailedImages(editor.iframeElement);
          // remove `data-mce-id="__mcenew" ` to fix infinite re-render
          content = content?.replace(/data-mce-id="__mcenew" /g, '') || '';
          try {
            const replacedValue: string | undefined = await inspectAndUpload(
              content
            );
            const newValue: string =
              typeof replacedValue === 'string' ? replacedValue : content;
            // avoid infinite re-render
            if (newValue !== textRef.current) {
              setValue(newValue);
            }
          } catch (error) {
            console.error('InlineHtml error:', error);
            if (error) {
              setErrorMessage(error.message);
              setValue(content);
            }
          }

          // submit every time a change occurred
          if (submitOnChange) {
            debounceSubmit(() => handleSubmit(), 100);
          }
        }}
        onKeyDown={event => {
          resetSessionTime();
          switch (event.key) {
            case 'Escape':
              return handleCancel();
          }
        }}
      />
      <Collapse in={!hideConfirm && didInit && (focus || buttonFocus)}>
        <StyledButtonGroup fullWidth color='primary' variant='text'>
          <Button
            fullWidth
            onFocus={() => setButtonFocus(true)}
            onBlur={() => setButtonFocus(false)}
            onClick={event => {
              clearDebouncedSubmit();
              event.stopPropagation();
              setButtonFocus(false);
              handleSubmit();
            }}
          >
            <DoneIcon />
          </Button>
          <Button
            fullWidth
            onFocus={() => setButtonFocus(true)}
            onBlur={() => setButtonFocus(false)}
            onClick={event => {
              clearDebouncedSubmit();
              event.stopPropagation();
              setFocus(false);
              setButtonFocus(false);
              handleCancel();
            }}
          >
            <ClearIcon />
          </Button>
        </StyledButtonGroup>
      </Collapse>
      {didInit && helperText && <FormHelperText>{helperText}</FormHelperText>}
      <InlineHtmlImageDialog
        open={isDialogOpen}
        attachments={attachments}
        onCompleteUpload={onCompleteUpload}
        onChangeAttachments={onChangeAttachments}
        onCancel={() => setIsDialogOpen(false)}
        onSelect={(url: string) => {
          setIsDialogOpen(false);
          setImageCallbackRef.current?.(url);
        }}
      />

      <AppSnackbar
        severity='error'
        open={!!errorMessage}
        onClose={() => setErrorMessage('')}
      >
        {errorMessage}
      </AppSnackbar>
    </Grid>
  );
};

export default InlineHtml;
